import {VanillaReaderUiDialog} from './VanillaReaderUiDialog';
import {ITreeViewItemSourceData, TreeView} from "../utils/TreeView";
import {
    VanillaUiActionEventCallback,
    VanillaUiDialogResultCallback
} from "../VanillaReaderUI/VanillaReaderUiModel";
import {IVanillaReaderUiAction, VanillaReaderUiActionNames} from "./VanillaReaderUiModel";
import {VanillaReaderUiActions} from "./VanillaReaderUiActions";

/**
 * # VanillaReaderUiDialogActions
 *
 * ## RESPONSIBILITIES
 *
 * ## RELATED TYPES
 * - VanillaReaderUiDialogActionsController
 *
 */

export class VanillaReaderUiDialogActions extends VanillaReaderUiDialog<void> {
    private _actionsListElement: HTMLElement;
    private _actionItemClickedCallback: VanillaUiActionEventCallback | undefined = undefined;
    private _treeViewActions: TreeView | undefined = undefined;
    private _dataListActions: HTMLDataListElement;
    private _formSearchActions: HTMLFormElement;
    private _inputSearchActions: HTMLInputElement;
    private _actionListFlat:  ITreeViewItemSourceData<IVanillaReaderUiAction | undefined>[];

    private _bodyStyle = `
        <style>
            
            #vanilla-reader__dialog-ui-actions__list ul[role=tree] {
                padding-left: 0;
                list-style: none;
            }
                        
            #vanilla-reader__dialog-ui-actions__list ul[role=tree] button {
                font-size: x-large;
                margin-left: 0;
            }

            #vanilla-reader__dialog-ui-actions__list ul[role=group] {
                padding-left: 1em;
                list-style: none;
            }
            
            #vanilla-reader__dialog-ui-actions__list ul[role=group] li:not([data-treeview-role=group]) {
               margin-left: 1em;
            }
            
            #vanilla-reader__dialog-ui-actions__list li {
                align-items: center;
            }

            #vanilla-reader__dialog-ui-actions__list li a {
                max-width: 70%;
            }

            #vanilla-reader__dialog-ui-actions__list li[aria-selected=true] {
                text-decoration: underline;
            }
            
            #vanilla-reader__dialog-ui-actions__list li[aria-expanded=true] ul {
                border-left: 1px dotted var(--vanilla-reader__color__ui__outline-dark);
            }
            
            #vanilla-reader__dialog-ui-actions__form-search {
                display: flex;
            }
            
            #vanilla-reader__dialog-ui-actions__input-search {
                flex-grow: 1;
            }

        </style>`;

    private _bodyTemplate = `
        <form id="vanilla-reader__dialog-ui-actions__form-search">                   
            <input type="search" name="input-search" placeholder="Search for action" list="vanilla-reader__dialog-ui-actions__datalist" id="vanilla-reader__dialog-ui-actions__input-search">
            <button id="vanilla-reader__dialog-ui-actions__button-search" aria-label="Do search" title="Do search"><span class="vanilla__button-icon" aria-hidden="true">search</span></button>
            <datalist id="vanilla-reader__dialog-ui-actions__datalist"></datalist>
        </form>        
        <section id="vanilla-reader__dialog-ui-actions__list"></section>`;

    constructor(
        _hostElement: HTMLElement,
        _insertPosition: InsertPosition = 'afterbegin',
    ) {
        super(_hostElement, 'vanilla-reader__dialog-ui-actions', 'App actions', _insertPosition);

        this._dialogBodyElement.innerHTML = this._bodyTemplate;
        this._dialogElement.insertAdjacentHTML('afterbegin', this._bodyStyle);
        this._formSearchActions = this._dialogElement.querySelector<HTMLFormElement>('#vanilla-reader__dialog-ui-actions__form-search')!;
        this._actionsListElement = this._dialogElement.querySelector<HTMLElement>('#vanilla-reader__dialog-ui-actions__list')!;
        this._dataListActions = this._dialogElement.querySelector<HTMLDataListElement>('#vanilla-reader__dialog-ui-actions__datalist')!;
        this._inputSearchActions = this._dialogElement.querySelector<HTMLInputElement>('#vanilla-reader__dialog-ui-actions__input-search')!;
        this._inputSearchActions.ariaLabel = `
            Search app actions. As you type, results will appear in an options list below the input field. 
            Press Shift + the Down Arrow key to step through the result list."`;


        this._formSearchActions.onsubmit = this._onActionSearchSubmitted;
        this._actionListFlat = [];
        this.populate();
    }

    show(silent: boolean = false, returnFocusOnClose: boolean = true, onDialogCloseCallbackOverride?: VanillaUiDialogResultCallback<void>, collapseAll: boolean = true): void {
        super.show(silent, returnFocusOnClose, onDialogCloseCallbackOverride);
        if(collapseAll) {
            this._treeViewActions?.collapseAll();
        }
    }

    close(silent: boolean = false, returnDialogResult: boolean = true): void {
        silent = true;
        super.close(silent, returnDialogResult);
        this._inputSearchActions.value = '';
    }

    onActionItemClicked(callback: VanillaUiActionEventCallback): void {
        this._actionItemClickedCallback = callback;
    }

    populate(): void {

        this._actionListFlat = VanillaReaderUiActions.flatMap((item)=>{
            return [item ,...item.children]
        });

        for(let item of this._actionListFlat) {
            if(item.data) {
                let optionElement = document.createElement('option');
                optionElement.text = item.textContent;
                optionElement.setAttribute('data-action-id', item.id);
                optionElement.setAttribute('data-action-name', item.data?.name || '');
                this._dataListActions.appendChild(optionElement);
            }
        }

        this._treeViewActions = new TreeView<IVanillaReaderUiAction | undefined>(this._actionsListElement, VanillaReaderUiActions);
        this._treeViewActions.cssClassName = 'vanilla-reader__dialog-ui-actions-list';

        this._treeViewActions.addItemClickEventHandler((actionItem: ITreeViewItemSourceData<IVanillaReaderUiAction>) => {
            if (this._actionItemClickedCallback && actionItem.data) {
                this._actionItemClickedCallback(actionItem.data.name as VanillaReaderUiActionNames);
            } else {
                let treeViewItem = this._treeViewActions?.treeViewItemDataToTreeViewItemMap.get(actionItem);
                if(treeViewItem) {
                    treeViewItem.setIsExpanded(!treeViewItem.getIsExpanded());
                }
            }
        });

        this._treeViewActions.render();
    }

    empty(): void {
        if (this._treeViewActions) {
            this._treeViewActions.destroy();
        }
    }

    _onActionSearchSubmitted = (ev: SubmitEvent) => {
        ev.preventDefault();
        if(this._actionItemClickedCallback && this._inputSearchActions.value) {
            let actionCommand: ITreeViewItemSourceData<IVanillaReaderUiAction | undefined> | undefined;

            actionCommand = this._actionListFlat?.find((itemData)=> {
                return itemData.textContent === this._inputSearchActions.value;
            });

            if(actionCommand && actionCommand.data) {
                this._actionItemClickedCallback(actionCommand.data.name);
            }
        }
        return false;
    }

}

